/**
 * Next-step CTA on ATS analysis results: rewrite the same file with AI.
 * When `onStartRewrite` is set, stays on this page (no second upload screen).
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { trackAtsPrepareAiClick } from '../utils/analytics';
import './AnalysisHelpForm.css';

const AnalysisHelpForm = ({
  jobPosition = '',
  compact = false,
  hero = false,
  cvId = '',
  fileName = '',
  onStartRewrite,
  rewriting = false,
}) => {
  const { t } = useLanguage();
  const job = (jobPosition || '').trim();
  const jobHint = job ? t('flow.helpJobHint').replace('{job}', job) : '';
  const hasStored = Boolean(cvId);
  const params = new URLSearchParams();
  if (hasStored) {
    params.set('fromCv', String(cvId));
    params.set('autostart', '1');
  }
  if (job) {
    params.set('job', job);
  }
  const search = params.toString() ? `?${params.toString()}` : '';
  const headingId = hero
    ? 'analysis-next-hero'
    : compact
      ? 'analysis-next-compact'
      : 'analysis-next-title';
  const className = [
    'analysis-help',
    compact ? 'is-compact' : '',
    hero ? 'is-hero' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const handleClick = () => {
    trackAtsPrepareAiClick({
      hasJob: Boolean(job),
      reuseFile: hasStored,
    });
    if (onStartRewrite) {
      onStartRewrite();
    }
  };

  return (
    <section className={className} aria-labelledby={headingId}>
      <div className="analysis-help__copy">
        <h3 id={headingId} className="analysis-help__title">
          {t('flow.helpTitle')}
        </h3>
        <p className="analysis-help__lead">
          {t(hasStored ? 'flow.helpLeadReuse' : 'flow.helpLead')}
        </p>
        {jobHint ? <p className="analysis-help__job">{jobHint}</p> : null}
        {hasStored && fileName ? (
          <p className="analysis-help__file">{fileName}</p>
        ) : null}
      </div>
      <div className="analysis-help__action">
        {onStartRewrite && hasStored ? (
          <button
            type="button"
            className="btn btn-primary analysis-help__cta"
            onClick={handleClick}
            disabled={rewriting}
            aria-busy={rewriting}
          >
            {rewriting ? t('flow.analyzing') : t('flow.helpSubmit')}
          </button>
        ) : (
          <Link
            to={{
              pathname: '/dashboard/quick-edit',
              search,
            }}
            state={
              job || hasStored
                ? {
                    ...(job ? { jobPosition: job } : {}),
                    ...(hasStored ? { fromCv: String(cvId) } : {}),
                  }
                : undefined
            }
            className="btn btn-primary analysis-help__cta"
            onClick={handleClick}
          >
            {t('flow.helpSubmit')}
          </Link>
        )}
        <p className="analysis-help__hint">{t('flow.helpHint')}</p>
      </div>
    </section>
  );
};

AnalysisHelpForm.propTypes = {
  jobPosition: PropTypes.string,
  compact: PropTypes.bool,
  hero: PropTypes.bool,
  cvId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fileName: PropTypes.string,
  onStartRewrite: PropTypes.func,
  rewriting: PropTypes.bool,
};

AnalysisHelpForm.defaultProps = {
  jobPosition: '',
  compact: false,
  hero: false,
  cvId: '',
  fileName: '',
  onStartRewrite: undefined,
  rewriting: false,
};

export default AnalysisHelpForm;
